import {useGlobalContext, useUserContext} from "../../hooks/globalContext";
import React, {useEffect, useState} from "react";
import Select from "react-select";
import {TextI18n} from "../input/textI18n";
import {SubmitButton} from "../input/submitButton";
import {AdditionalSettings, CrutchSettings, OrgCrutches} from "../../models/requests/additionalSettings";
import {addNotification} from "../../utils/toast/toast";
import {Checkbox} from "../input/checkbox";
import { DeleteButton } from "components/input/deleteButton";
import {LangsList} from "../../migration/entities/base/mlString";

const DEFAULT_BTN_TEXT: { [key: string]: string } = {
    "KZ": "Келісемін",
    "RU": "Согласен(-на)",
    "EN": "Agree",
    "TR": "Kabul ediyorum",
    "AZ": "Razıyam",
    "CH": "同意",
    "JP": "同意します",
    "AR": "أوافق",
    "UZ": "Roziman",
    "ID": "Setuju",
    "KY": "Макулмун",
    "UK": "Погоджуюсь"
};

type MenusMap = { all: CrutchSettings | null; [menuId: string]: CrutchSettings | null };

export function AdditionalSettingsModule() {
    const {user, menu} = useUserContext();
    const {i18n, api} = useGlobalContext();

    const isAdmin = !!(user?.isStaff || user?.user?.isSuper);

    const [loading, setLoading] = useState<boolean>(false);
    const [orgCrutches, setOrgCrutches] = useState<OrgCrutches | null>(null);

    const [menus, setMenus] = useState<{ value: string; label: string }[]>([
        {value: "all", label: "all"},
    ]);

    const [selectedMenuId, setSelectedMenuId] = useState<string>("all");
    const [activeInnerTab, setActiveInnerTab] = useState<"modal" | "crutches">("crutches");

    useEffect(() => {
        if (user?.org?.menus?.menus) {
            setMenus([
                {value: "all", label: "all"},
                ...user.org.menus.menus.map((m: any) => ({
                    value: m.id,
                    label: m.name,
                })),
            ]);
        }
    }, [user]);

    useEffect(() => {
        if (!isAdmin || !user?.org?.id) return;
        const controller = new AbortController();
        api.getOrgCrutches({controller, setLoading}, user.org.subdomain).then((res) => {
            if (res.data) {
                setOrgCrutches(res.data);
            }
        });
        return () => controller.abort();
    }, [api, isAdmin, user]);

    const getScopeKey = () => selectedMenuId || "all";

    const getMenusMap = (): MenusMap => {
        return (orgCrutches?.menus as MenusMap) || {all: null};
    };

    const currentSettings = (getMenusMap()[getScopeKey()] || null) as CrutchSettings | null;
    const isOrganizationScope = selectedMenuId === "all";
    const titleLanguages = isOrganizationScope ? Object.values(LangsList) : menu.i18n.otherLangs || [];
    const organizationCarouselEnabled = orgCrutches?.menus?.all?.recommendationCarousel?.enabled ?? false;
    const carouselEnabled = currentSettings?.recommendationCarousel?.enabled
        ?? organizationCarouselEnabled;

    const updateCrutchSettings = (updater: (prev: CrutchSettings) => CrutchSettings) => {
        const scopeKey = getScopeKey();
        setOrgCrutches((prev): OrgCrutches => {
            const base: OrgCrutches = prev || {_id: "", menus: {all: null}};
            const currentMenus: MenusMap = (base.menus as MenusMap) || {all: null};
            const existing = (currentMenus[scopeKey] || {}) as CrutchSettings;
            const nextSettings = updater(existing);

            return {
                ...base,
                menus: {
                    ...currentMenus,
                    [scopeKey]: nextSettings,
                },
            };
        });
    };

    const handleSelectMenu = (option: { value: string; label: string } | null) => {
        if (!option) return;
        setSelectedMenuId(option.value);
    };

    const handleSave = async () => {
        const payload: AdditionalSettings = {
            orgId: user?.org?.id || "",
            data: orgCrutches?.menus || null,
        };
        const result = await api.addOrgCrutches({setLoading}, payload);
        if (!result?.success) return;
        addNotification({message: "Настройки успешно сохранены", type: "success"});
    };

    const handleDelete = async () => {
        const scopeKey = getScopeKey();
        const currentMenus = getMenusMap();
        const newMenus = {
            ...currentMenus,
            [scopeKey]: null,
        };

        const payload: AdditionalSettings = {
            orgId: user?.org?.id || "",
            data: newMenus,
        };
        const result = await api.addOrgCrutches({setLoading}, payload);
        if (!result?.success) return;
        setOrgCrutches((prev) => ({
            ...(prev || {_id: "", menus: {all: null}}),
            menus: newMenus as OrgCrutches["menus"],
        }));
        addNotification({message: "Данные меню удалены", type: "success"});
    };

    if (!isAdmin) {
        return null;
    }

    return (
        <div className="form">

            <h2 className="app__header-title" style={{marginBottom: 16}}>Костыли</h2>

            {/* <ul className="nav default-tabs mb-2 mb-lg-3">
                <li className="default-tabs__item">
                    <a
                        href="#"
                        className={activeInnerTab === "modal" ? "default-tabs__link active" : "default-tabs__link "}
                        onClick={(e) => {
                            e.preventDefault();
                            setActiveInnerTab("modal");
                        }}
                    >
                        Модальное окно
                    </a>
                </li>
                <li className="default-tabs__item">
                    <a
                        href="#"
                        className={activeInnerTab === "crutches" ? "default-tabs__link active" : "default-tabs__link "}
                        onClick={(e) => {
                            e.preventDefault();
                            setActiveInnerTab("crutches");
                        }}
                    >
                        Костыли
                    </a>
                </li>
            </ul> */}

            {activeInnerTab === "modal" && (
                <form className="form">
                    <div className="control">
                        <Select
                            options={menus}
                            placeholder="Выберите меню или «Все меню»"
                            value={menus.find((m) => m.value === selectedMenuId) || null}
                            onChange={handleSelectMenu}
                        />
                    </div>

                    <TextI18n
                        key={`modalMessage.text-${selectedMenuId}`}
                        values={currentSettings?.modalMessage?.text || null}
                        setValues={(v) =>
                            updateCrutchSettings((prev) => ({
                                ...prev,
                                modalMessage: {
                                    ...(prev.modalMessage || {title: {}, text: {}, btnText: DEFAULT_BTN_TEXT}),
                                    text: v,
                                },
                            }))
                        }
                        lang={menu.i18n.defaultLang}
                        isStaff={true}
                        allLang={titleLanguages}
                        placeholder="Введите текст модального окна"
                        label="Текст модального окна"
                    />

                    <div className="form__buttons mt-4">
                        <div className="form__buttons-left">
                            <SubmitButton
                                onSubmit={handleSave}
                                label="Сохранить"
                                disabled={false}
                                isLoading={loading}
                            />
                            <DeleteButton
                                submitLoading={loading}
                                onSubmit={handleDelete}
                                label="Удалить"
                                disabled={loading}
                            />
                        </div>
                    </div>
                </form>
            )}

            {activeInnerTab === "crutches" && (
                <form className="form">
                    <div className="control">
                        <Select
                            options={menus}
                            placeholder="Выберите меню или «Все меню»"
                            value={menus.find((m) => m.value === selectedMenuId) || null}
                            onChange={handleSelectMenu}
                        />
                    </div>

                    <h3 className="mt-4">Страница успеха</h3>

                    <TextI18n
                        key={`successPageTitle-${selectedMenuId}`}
                        label="Заголовок"
                        values={currentSettings?.successPageTitle || null}
                        setValues={(v) =>
                            updateCrutchSettings((prev) => ({
                                ...prev,
                                successPageTitle: v,
                            }))
                        }
                        lang={menu.i18n.defaultLang}
                        allLang={titleLanguages}
                        isStaff={true}
                        placeholder="Введите заголовок"
                    />

                    <TextI18n
                        key={`successPageSubTitle-${selectedMenuId}`}
                        label="Подзаголовок"
                        values={currentSettings?.successPageSubTitle || null}
                        setValues={(v) =>
                            updateCrutchSettings((prev) => ({
                                ...prev,
                                successPageSubTitle: v,
                            }))
                        }
                        lang={menu.i18n.defaultLang}
                        allLang={titleLanguages}
                        isStaff={true}
                        placeholder="Введите подзаголовок"
                    />

                    <h3 className="mt-4">Модальное окно</h3>

                    <TextI18n
                        key={`modalMessage.title-${selectedMenuId}`}
                        label="Заголовок"
                        values={currentSettings?.modalMessage?.title || null}
                        setValues={(v) =>
                            updateCrutchSettings((prev) => ({
                                ...prev,
                                modalMessage: {
                                    ...(prev.modalMessage || {title: {}, text: {}, btnText: DEFAULT_BTN_TEXT}),
                                    title: v,
                                },
                            }))
                        }
                        lang={menu.i18n.defaultLang}
                        allLang={titleLanguages}
                        isStaff={true}
                        placeholder="Введите заголовок"
                    />

                    <TextI18n
                        key={`modalMessage.text-${selectedMenuId}`}
                        label="Текст"
                        values={currentSettings?.modalMessage?.text || null}
                        setValues={(v) =>
                            updateCrutchSettings((prev) => ({
                                ...prev,
                                modalMessage: {
                                    ...(prev.modalMessage || {title: {}, text: {}, btnText: DEFAULT_BTN_TEXT}),
                                    text: v,
                                },
                            }))
                        }
                        lang={menu.i18n.defaultLang}
                        allLang={titleLanguages}
                        isStaff={true}
                        placeholder="Введите текст"
                    />

                    <TextI18n
                        key={`modalMessage.btnText-${selectedMenuId}`}
                        label="Текст кнопки"
                        values={currentSettings?.modalMessage?.btnText || null}
                        setValues={(v) =>
                            updateCrutchSettings((prev) => ({
                                ...prev,
                                modalMessage: {
                                    ...(prev.modalMessage || {title: {}, text: {}, btnText: DEFAULT_BTN_TEXT}),
                                    btnText: v,
                                },
                            }))
                        }
                        lang={menu.i18n.defaultLang}
                        allLang={titleLanguages}
                        isStaff={true}
                        placeholder="Введите текст"
                    />

                    <h3 className="mt-4">Корзина</h3>

                    <Checkbox
                        label="Отключить корзину"
                        checked={currentSettings?.disableCart ?? false}
                        onChange={(checked) =>
                            updateCrutchSettings((prev) => ({
                                ...prev,
                                disableCart: checked,
                            }))
                        }
                    />

                    <Checkbox
                        label="Скрыть кнопку корзины"
                        checked={currentSettings?.hideCartBtn ?? false}
                        onChange={(checked) =>
                            updateCrutchSettings((prev) => ({
                                ...prev,
                                hideCartBtn: checked,
                            }))
                        }
                    />

                    <Checkbox
                        label="Не создавать заказ (только WhatsApp)"
                        checked={currentSettings?.skipOrderCreation ?? false}
                        onChange={(checked) =>
                            updateCrutchSettings((prev) => ({
                                ...prev,
                                skipOrderCreation: checked,
                            }))
                        }
                    />

                    <Checkbox
                        label="WhatsApp сразу из корзины (без формы заказа)"
                        checked={currentSettings?.whatsappFromCart ?? false}
                        onChange={(checked) =>
                            updateCrutchSettings((prev) => ({
                                ...prev,
                                whatsappFromCart: checked,
                            }))
                        }
                    />

                    <TextI18n
                        key={`cartBtnText-${selectedMenuId}`}
                        label="Текст кнопки корзины"
                        values={currentSettings?.cartBtnText || null}
                        setValues={(v) =>
                            updateCrutchSettings((prev) => ({
                                ...prev,
                                cartBtnText: v,
                            }))
                        }
                        lang={menu.i18n.defaultLang}
                        allLang={titleLanguages}
                        isStaff={true}
                        placeholder="Введите текст"
                    />

                    <h3 className="mt-4">Варианты доставки</h3>

                    <Checkbox
                        label="Запретить гостю менять размещение из QR-ссылки"
                        checked={currentSettings?.lockPlacementFromUrl ?? false}
                        onChange={(checked) =>
                            updateCrutchSettings((prev) => ({
                                ...prev,
                                lockPlacementFromUrl: checked,
                            }))
                        }
                    />

                    <TextI18n
                        key={`deliveryVariantTitles.city-${selectedMenuId}`}
                        label="Город"
                        values={currentSettings?.deliveryVariantTitles?.city || null}
                        setValues={(v) =>
                            updateCrutchSettings((prev) => ({
                                ...prev,
                                deliveryVariantTitles: {
                                    ...(prev.deliveryVariantTitles || {
                                        city: {},
                                        selfPickupEmpty: {},
                                        customPlace: {},
                                        placements: {},
                                        cinema: {},
                                    }),
                                    city: v,
                                },
                            }))
                        }
                        lang={menu.i18n.defaultLang}
                        allLang={titleLanguages}
                        isStaff={true}
                        placeholder="Введите текст"
                    />

                    <TextI18n
                        key={`deliveryVariantTitles.selfPickupEmpty-${selectedMenuId}`}
                        label="Самовывоз: пустое состояние"
                        values={currentSettings?.deliveryVariantTitles?.selfPickupEmpty || null}
                        setValues={(v) =>
                            updateCrutchSettings((prev) => ({
                                ...prev,
                                deliveryVariantTitles: {
                                    ...(prev.deliveryVariantTitles || {
                                        city: {},
                                        selfPickupEmpty: {},
                                        customPlace: {},
                                        placements: {},
                                        cinema: {},
                                    }),
                                    selfPickupEmpty: v,
                                },
                            }))
                        }
                        lang={menu.i18n.defaultLang}
                        allLang={titleLanguages}
                        isStaff={true}
                        placeholder="Введите текст"
                    />

                    <TextI18n
                        key={`deliveryVariantTitles.customPlace-${selectedMenuId}`}
                        label="Свое место"
                        values={currentSettings?.deliveryVariantTitles?.customPlace || null}
                        setValues={(v) =>
                            updateCrutchSettings((prev) => ({
                                ...prev,
                                deliveryVariantTitles: {
                                    ...(prev.deliveryVariantTitles || {
                                        city: {},
                                        selfPickupEmpty: {},
                                        customPlace: {},
                                        placements: {},
                                        cinema: {},
                                    }),
                                    customPlace: v,
                                },
                            }))
                        }
                        lang={menu.i18n.defaultLang}
                        allLang={titleLanguages}
                        isStaff={true}
                        placeholder="Введите текст"
                    />

                    <TextI18n
                        key={`deliveryVariantTitles.placements-${selectedMenuId}`}
                        label="Заведения"
                        values={currentSettings?.deliveryVariantTitles?.placements || null}
                        setValues={(v) =>
                            updateCrutchSettings((prev) => ({
                                ...prev,
                                deliveryVariantTitles: {
                                    ...(prev.deliveryVariantTitles || {
                                        city: {},
                                        selfPickupEmpty: {},
                                        customPlace: {},
                                        placements: {},
                                        cinema: {},
                                    }),
                                    placements: v,
                                },
                            }))
                        }
                        lang={menu.i18n.defaultLang}
                        allLang={titleLanguages}
                        isStaff={true}
                        placeholder="Введите текст"
                    />

                    <TextI18n
                        key={`deliveryVariantTitles.cinema-${selectedMenuId}`}
                        label="Кинотеатр"
                        values={currentSettings?.deliveryVariantTitles?.cinema || null}
                        setValues={(v) =>
                            updateCrutchSettings((prev) => ({
                                ...prev,
                                deliveryVariantTitles: {
                                    ...(prev.deliveryVariantTitles || {
                                        city: {},
                                        selfPickupEmpty: {},
                                        customPlace: {},
                                        placements: {},
                                        cinema: {},
                                    }),
                                    cinema: v,
                                },
                            }))
                        }
                        lang={menu.i18n.defaultLang}
                        allLang={titleLanguages}
                        isStaff={true}
                        placeholder="Введите текст"
                    />

                    <h3 className="mt-4">Время отключения</h3>

                    <div className="control">
                        <label className="control__label">С</label>
                        <input
                            className="control__input"
                            type="time"
                            value={currentSettings?.disableTime?.from || ""}
                            onChange={(e) =>
                                updateCrutchSettings((prev) => ({
                                    ...prev,
                                    disableTime: {
                                        ...(prev.disableTime || {from: "", to: ""}),
                                        from: e.target.value,
                                    },
                                }))
                            }
                        />
                    </div>

                    <div className="control">
                        <label className="control__label">До</label>
                        <input
                            className="control__input"
                            type="time"
                            value={currentSettings?.disableTime?.to || ""}
                            onChange={(e) =>
                                updateCrutchSettings((prev) => ({
                                    ...prev,
                                    disableTime: {
                                        ...(prev.disableTime || {from: "", to: ""}),
                                        to: e.target.value,
                                    },
                                }))
                            }
                        />
                    </div>

                    <h3 className="mt-4">Минимальный заказ</h3>

                    <div className="control">
                        <label className="control__label">Сумма</label>
                        <input
                            className="control__input"
                            type="number"
                            value={currentSettings?.minOrder?.price ?? 0}
                            onChange={(e) =>
                                updateCrutchSettings((prev) => ({
                                    ...prev,
                                    minOrder: {
                                        ...(prev.minOrder || {price: 0, text: {}}),
                                        price: Number(e.target.value),
                                    },
                                }))
                            }
                        />
                    </div>

                    <TextI18n
                        key={`minOrder.text-${selectedMenuId}`}
                        label="Текст"
                        values={currentSettings?.minOrder?.text || null}
                        setValues={(v) =>
                            updateCrutchSettings((prev) => ({
                                ...prev,
                                minOrder: {
                                    ...(prev.minOrder || {price: 0, text: {}}),
                                    text: v,
                                },
                            }))
                        }
                        lang={menu.i18n.defaultLang}
                        allLang={titleLanguages}
                        isStaff={true}
                        placeholder="Введите текст"
                    />

                    {!isOrganizationScope && <>
                        <h3 className="mt-4">Вызов персонала (официант)</h3>
                        {[
                            ["waiter", "Позвать официанта"],
                            ["check_cash", "Принести счёт — наличные"],
                            ["check_card", "Принести счёт и терминал — карта"],
                            ["manager", "Позвать менеджера"],
                        ].map(([action, label], _, options) => <Checkbox key={action} label={label}
                            checked={currentSettings?.serviceRequests?.actions?.includes(action) || false}
                            onChange={(checked) => updateCrutchSettings((prev) => {
                                const actions = options.map(([value]) => value).filter(value => value === action
                                    ? checked : prev.serviceRequests?.actions?.includes(value));
                                const next = {...prev};
                                if (actions.length) next.serviceRequests = {actions};
                                else delete next.serviceRequests;
                                return next;
                            })}/>) }
                    </>}

                    <h3 className="mt-4">Карусель рекомендаций</h3>

                    <p>
                        Показывает рекомендации перед первой категорией меню. Используется общий список
                        «Общий» из раздела кросс-продаж (товары, рекомендуемые всегда). Изменение этого списка также влияет
                        на ручные рекомендации в карточке товара и корзине.
                    </p>

                    <div className="control">
                        <label className="control__label" htmlFor="recommendation-carousel-enabled">Показывать карусель</label>
                        <select
                            id="recommendation-carousel-enabled"
                            className="control__input"
                            value={currentSettings?.recommendationCarousel?.enabled === undefined
                                ? "inherit"
                                : String(currentSettings.recommendationCarousel.enabled)}
                            onChange={(e) => {
                                const value = e.target.value;
                                updateCrutchSettings((prev) => {
                                    const recommendationCarousel = {...prev.recommendationCarousel};
                                    if (value === "inherit") {
                                        delete recommendationCarousel.enabled;
                                    } else {
                                        recommendationCarousel.enabled = value === "true";
                                    }
                                    return {...prev, recommendationCarousel};
                                });
                            }}
                        >
                            <option value="inherit">{isOrganizationScope
                                ? "По умолчанию (выключена)"
                                : `Как для всех меню (${organizationCarouselEnabled ? "включена" : "выключена"})`}</option>
                            <option value="true">Включена</option>
                            <option value="false">Выключена</option>
                        </select>
                        <p>{carouselEnabled ? "Карусель включена. Если доступных товаров нет, она не показывается." : "Карусель выключена."}</p>
                    </div>

                    <TextI18n
                        key={`recommendation-carousel-title-${selectedMenuId}`}
                        label="Заголовок карусели (необязательно)"
                        values={currentSettings?.recommendationCarousel?.title || null}
                        setValues={(values) => updateCrutchSettings((prev) => ({
                            ...prev,
                            recommendationCarousel: {
                                ...prev.recommendationCarousel,
                                title: Object.fromEntries(Object.entries(values).filter(([, value]) => value.trim())),
                            },
                        }))}
                        lang={menu.i18n.defaultLang}
                        allLang={titleLanguages}
                        isStaff={true}
                        placeholder="Рекомендуем"
                    />
                    <p>{isOrganizationScope
                        ? "Оставьте перевод пустым, чтобы использовать стандартный заголовок на этом языке."
                        : "Пустой перевод наследует заголовок для всех меню. Если его нет, используется стандартный заголовок на этом языке."}</p>

                    <h3 className="mt-4">Прочее</h3>

                    <Checkbox
                        label="Не переходить по ссылке Kami"
                        checked={currentSettings?.preventKamiLinkFollow ?? false}
                        onChange={(checked) =>
                            updateCrutchSettings((prev) => ({
                                ...prev,
                                preventKamiLinkFollow: checked,
                            }))
                        }
                    />

                    <div className="control">
                        <label className="control__label">Версия рекомендательной системы</label>
                        <select
                            className="control__input"
                            value={currentSettings?.recommendationSystemVersion || "v1"}
                            onChange={(e) =>
                                updateCrutchSettings((prev) => ({
                                    ...prev,
                                    recommendationSystemVersion: e.target.value as "v1" | "manual",
                                }))
                            }
                        >
                            <option value="v1">v1</option>
                            <option value="manual">ручная</option>
                        </select>
                    </div>

                    <Checkbox
                        label="Включить комментарий при положительной оценке"
                        checked={currentSettings?.enableCommentWithPositiveGrading ?? false}
                        onChange={(checked) =>
                            updateCrutchSettings((prev) => ({
                                ...prev,
                                enableCommentWithPositiveGrading: checked,
                            }))
                        }
                    />

                    <Checkbox
                        label="Включить опрос"
                        checked={currentSettings?.enableSurvey ?? false}
                        onChange={(checked) =>
                            updateCrutchSettings((prev) => ({
                                ...prev,
                                enableSurvey: checked,
                            }))
                        }
                    />

                    <Checkbox
                        label="Чёрный прелоадер"
                        checked={currentSettings?.cbLogo ?? false}
                        onChange={(checked) =>
                            updateCrutchSettings((prev) => ({
                                ...prev,
                                cbLogo: checked,
                            }))
                        }
                    />

                    <div className="control">
                        <label className="control__label">Текст вместо логотипа на прелоадере</label>
                        <input
                            className="control__input"
                            type="text"
                            value={currentSettings?.textOnPreloader ?? ""}
                            onChange={(e) =>
                                updateCrutchSettings((prev) => ({
                                    ...prev,
                                    textOnPreloader: e.target.value,
                                }))
                            }
                        />
                    </div>

                    <Checkbox
                        label="Отображать имя позиции справа"
                        checked={currentSettings?.nameDisplayRight ?? false}
                        onChange={(checked) =>
                            updateCrutchSettings((prev) => ({
                                ...prev,
                                nameDisplayRight: checked,
                            }))
                        }
                    />

                    <div className="form__buttons mt-4">
                        <div className="form__buttons-left">
                            <SubmitButton
                                onSubmit={handleSave}
                                label="Сохранить"
                                disabled={false}
                                isLoading={loading}
                            />
                            <DeleteButton
                                submitLoading={loading}
                                onSubmit={handleDelete}
                                label="Удалить"
                                disabled={loading}
                            />
                        </div>
                    </div>
                </form>
            )}
        </div>
    );
}
